import React, { useState, useRef, useEffect } from 'react';
import { Tag, Plus, X, ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';

const TagSelector = ({ selectedTagIds = [], availableTags = [], onChange, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [isOpen]);

  const toggleTag = (tagId) => {
    const updated = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter(id => id !== tagId)
      : [...selectedTagIds, tagId];
    onChange(updated);
  };

  const selectedTags = availableTags.filter(t => selectedTagIds.includes(t.id)).sort((a, b) => a.name.localeCompare(b.name));
  const unselectedTags = availableTags.filter(t => !selectedTagIds.includes(t.id)).sort((a, b) => a.name.localeCompare(b.name));

  if (compact) {
    return (
      <div className="relative inline-block" ref={triggerRef} data-testid="tag-selector-compact">
        {/* Display selected tags inline */}
        <div className="flex flex-wrap gap-1 items-center">
          {selectedTags.map(tag => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-default"
              style={{ backgroundColor: tag.color, color: tag.text_color }}
              data-testid={`selected-tag-${tag.id}`}
            >
              {tag.name}
              <button
                onClick={(e) => { e.stopPropagation(); toggleTag(tag.id); }}
                className="hover:opacity-70 transition-opacity"
              >
                <X size={10} />
              </button>
            </span>
          ))}
          <button
            data-testid="tag-add-trigger"
            onClick={() => setIsOpen(!isOpen)}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          >
            <Plus size={12} />
            <span>Tag</span>
          </button>
        </div>

        {isOpen && unselectedTags.length > 0 && createPortal(
          <div ref={dropdownRef} className="fixed w-48 bg-white border border-gray-200 rounded-lg shadow-xl py-1 max-h-48 overflow-y-auto" style={{ top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999 }} data-testid="tag-dropdown">
            {unselectedTags.map(tag => (
              <button
                key={tag.id}
                onClick={() => toggleTag(tag.id)}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 text-left transition-colors"
                data-testid={`tag-option-${tag.id}`}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="text-sm text-gray-700 truncate">{tag.name}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Full form version
  return (
    <div className="relative" ref={dropdownRef} data-testid="tag-selector">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        <Tag size={14} className="inline mr-1" />
        Tags
      </label>

      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedTags.map(tag => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: tag.color, color: tag.text_color }}
              data-testid={`selected-tag-${tag.id}`}
            >
              {tag.name}
              <button onClick={() => toggleTag(tag.id)} className="hover:opacity-70">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown trigger */}
      <button
        type="button"
        data-testid="tag-selector-trigger"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
      >
        <span>{selectedTags.length > 0 ? `${selectedTags.length} tag(s) selected` : 'Select tags...'}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto" data-testid="tag-dropdown">
          {availableTags.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-3">No tags available. Create tags in Settings.</p>
          ) : (
            [...availableTags].sort((a, b) => a.name.localeCompare(b.name)).map(tag => {
              const isSelected = selectedTagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 text-left transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                  data-testid={`tag-option-${tag.id}`}
                >
                  <span
                    className="w-4 h-4 rounded-full flex-shrink-0 border"
                    style={{ backgroundColor: tag.color, borderColor: isSelected ? '#3B82F6' : 'transparent' }}
                  />
                  <span className="text-sm text-gray-700 flex-1">{tag.name}</span>
                  {isSelected && (
                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default TagSelector;
